import { Alert, Button, Center, Flex, Group, Loader, Modal, ScrollArea, Stack, Text } from "@mantine/core";
import { IIssueBoardApi } from "@schemas/issueBoardSchema";
import { IIssueApi } from "@schemas/issueSchema";
import { IconAlertCircle, IconLock } from "@tabler/icons-react";
import React, { useCallback, useEffect, useState } from "react";
import { useGetProjectByProjectIdIssuesGetSingleQuery } from "../../../../../../../openapi";
import { IssueIcon } from "../../../../../../components/CustomIcons/CustomIcons";
import { addAppModalThunk, setHasUnsavedCommentInRedux } from "../../../../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../../../../hooks";
import { AppModalEnums } from "../../../../../../mc-shared/sharedConstants";
import { getIssuesWithFormulaAdded, issueBoardContainsFormula } from "../../../../../../mc-shared/sharedUtils";
import LocalErrorBoundary from "../../../../../../util/LocalErrorBoundary";
import { useGetSingleIssueBoardQuery, useGetSumsByFormulaQuery } from "../../../IssueBoardRTK";
import { IssueTabsEnum } from "../../../issueConstants";
import { getIssuesWithMathFormulaAdded } from "../../../issueMathFormulaUtils";
import { getIssueRegisterNameOption, issueBoardHasClientAccess } from "../../../issueUtils";
import { IssueItemButtonGroups } from "../IssueItem/IssueItemButtonGroupsNew";
import IssueModalActions from "./IssueItemModalActions";
import IssueItemModalFiles from "./IssueItemModalFiles";
import IssueModalInfo from "./IssueItemModalInfo";
import IssueModalLogs from "./IssueItemModalLogs";
import IssueItemModalMenu from "./IssueItemModalMenu";
import IssueWorkflowProgress from "./IssueWorkflowProgress";
import { ModalTitle } from "./ModalTitle";
import { setClickedRoomNumberInModalInRedux } from "../../Floorplan/floorplanSlice";

export type IIssueModalSectionProps = {
  issueBoard: IIssueBoardApi;
  issue: IIssueApi;
  projectId: string;
};

const IssueItemModalMantine: React.FC<{
  itemId: string;
  issueBoardId: string;
  projectId: string;
  opened?: boolean;
  onClose?: () => void;
}> = ({ itemId, issueBoardId, projectId, onClose, opened = true }) => {
  const dispatch = useAppDispatch();
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const {
    data: issue,
    error: issueError,
    isLoading: isLoadingIssue,
  } = useGetProjectByProjectIdIssuesGetSingleQuery(
    {
      issueId: itemId,
      projectId,
    },
    { skip: itemId == null || projectId == null },
  );

  const {
    data: issueBoard,
    error: issueBoardError,
    isLoading: isLoadingIssueBoard,
  } = useGetSingleIssueBoardQuery(
    {
      projectId: projectId,
      issueBoardId: issueBoardId,
    },
    { skip: issueBoardId == null || projectId == null },
  );

  const issueBoardContainsFormulaFlag = issueBoardContainsFormula(issueBoard);

  const { data: sumsByFormula } = useGetSumsByFormulaQuery(
    {
      issueBoardId: issueBoardId,
      projectId: projectId,
    },
    {
      skip: issueBoardContainsFormulaFlag === false,
    },
  );

  const issuesWithFormulaAdded = issue
    ? getIssuesWithFormulaAdded({
        issues: [issue],
        issueBoard: issueBoard,
        sumsByFormula: sumsByFormula,
      })
    : [];

  const issuesWithAllFormulaAdded =
    issueBoard != null
      ? getIssuesWithMathFormulaAdded({
          issues: issuesWithFormulaAdded,
          issueBoard,
        })
      : issuesWithFormulaAdded;

  const issueErrorMessage = (issueError as any)?.data?.message ?? (issueError as any)?.message;
  const issueIsUnavailableForClient =
    (issueError as any)?.status === 403 ||
    issueErrorMessage === "No client access" ||
    issueErrorMessage === "Denne saken er utilgjengelig for kunder";

  const errorMessage = issueIsUnavailableForClient ? (
    <Center flex={1}>
      <Stack align="center" gap="xs">
        <IconLock color="var(--mantine-color-red-6)" size={28} />
        <Text fw={600}>Denne saken er utilgjengelig for kunder</Text>
      </Stack>
    </Center>
  ) : (
    (issueError != null || issueBoardError != null) && (
      <Alert flex={1} variant="light" color="red" title="Feil" icon={<IconAlertCircle />}>
        <pre>Feil ved henting av sak: {JSON.stringify(issueError ?? issueBoardError ?? "Ukjent feil")}</pre>
      </Alert>
    )
  );
  const hasUnsavedComment = useAppSelector((state) => state.globalReducer.hasUnsavedComment);
  const issueWithCalculatedFields = issuesWithAllFormulaAdded[0] ?? issue;

  const closeIssueModal = useCallback(() => {
    dispatch(setClickedRoomNumberInModalInRedux({ issueBoardId, clickedRoomNumberInModal: null }));
    onClose?.();
  }, [dispatch, issueBoardId, onClose]);

  useEffect(() => {
    dispatch(setClickedRoomNumberInModalInRedux({ issueBoardId, clickedRoomNumberInModal: null }));
  }, [dispatch, issueBoardId, itemId]);

  const onConfirmClose = () => {
    setConfirmModalOpen(false);
    dispatch(setHasUnsavedCommentInRedux(false));
    closeIssueModal();
  };

  return (
    <Modal
      closeOnEscape={false}
      size={"80vw"}
      onClose={() => {
        if (hasUnsavedComment === true) {
          setConfirmModalOpen(true);
          return;
        }
        closeIssueModal();
      }}
      styles={{
        close: {
          marginBottom: "0.5rem",
        },
        content: {
          maxWidth: "72rem",
          height: "95vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
        title: {
          width: "100%",
          textOverflow: "ellipsis",
          overflow: "hidden",
          whiteSpace: "nowrap",
        },
        body: {
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
        header: {
          minHeight: "5rem",
          paddingBottom: "1rem",
          paddingTop: "1rem",
        },
      }}
      opened={opened}
      title={
        isLoadingIssue || isLoadingIssueBoard || issue == null ? null : (
          <LocalErrorBoundary id="IssueModalTitle">
            <ModalTitle
              issue={issueWithCalculatedFields}
              projectId={projectId}
              showClientLock={issueBoardHasClientAccess(issueBoard)}
            />
          </LocalErrorBoundary>
        )
      }
      yOffset="1vh"
      centered
    >
      <Modal opened={confirmModalOpen} onClose={() => setConfirmModalOpen(false)} title="Ulagret kommentar">
        <Text>Du har en ulagret kommentar. Er du sikker på at du vil stenge uten å lagre?</Text>
        <Group justify="space-between" mt="md">
          <Button variant="outline" onClick={() => setConfirmModalOpen(false)}>
            Avbryt
          </Button>
          <Button color="red" onClick={() => onConfirmClose()}>
            Steng uten å lagre
          </Button>
        </Group>
      </Modal>
      {errorMessage}
      {(isLoadingIssue != false || isLoadingIssueBoard != false) && (
        <Center>
          <Loader size="sm" />
        </Center>
      )}
      {issue != null && issueBoard != null && !issueIsUnavailableForClient && (
        <LocalErrorBoundary id="IssueItemModalContent">
          <IssueItemModalContent issue={issueWithCalculatedFields} projectId={projectId} issueBoard={issueBoard} />
        </LocalErrorBoundary>
      )}
    </Modal>
  );
};

export default IssueItemModalMantine;

const IssueItemModalContent: React.FC<{
  issue: IIssueApi;
  issueBoard: IIssueBoardApi;
  projectId: string;
}> = ({ issue, projectId, issueBoard }) => {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<IssueTabsEnum>(IssueTabsEnum.INFO);

  const arcgis = useAppSelector((state) => state.adminReducer.project?.arcgis);
  const projectIsGisActivated = arcgis?.groupId != null;
  const parentIssue = issue.taskOrIssue === "TASK" ? issue.connectedIssue : null;

  const renderTabContent = () => {
    switch (activeTab) {
      case IssueTabsEnum.INFO:
        return <IssueModalInfo issueBoard={issueBoard} issue={issue} projectId={projectId} />;

      case IssueTabsEnum.ACTIONS:
        return <IssueModalActions issueBoard={issueBoard} issue={issue} projectId={projectId} />;

      case IssueTabsEnum.FILES:
        return <IssueItemModalFiles issue={issue} projectId={projectId} />;

      case IssueTabsEnum.LOG:
        return <IssueModalLogs issueBoard={issueBoard} issue={issue} projectId={projectId} />;

      default:
        return null;
    }
  };

  return (
    <Stack h="100%" gap="xs" style={{ overflow: "hidden" }}>
      <IssueWorkflowProgress />
      <Flex gap="xs" w="100%">
        <IssueItemButtonGroups
          issue={issue}
          activeButtonName={activeTab}
          setSelectedButtonName={(view) => setActiveTab(view)}
          taskOrIssue={issue.taskOrIssue}
          projectIsGisActivated={projectIsGisActivated}
          issueBoard={issueBoard}
        />

        <IssueItemModalMenu issueBoard={issueBoard} issue={issue} projectId={projectId} />
      </Flex>

      {parentIssue != null && (
        <Group gap={4} wrap="nowrap">
          <Text c="gray.7" fw={600} size="sm">
            Del av {getIssueRegisterNameOption(issueBoard?.issueNameSetting, "singular")?.toLowerCase()}:
          </Text>
          <Button
            color="mc-primary"
            variant="subtle"
            size="compact-sm"
            px={4}
            onClick={() =>
              dispatch(
                addAppModalThunk({
                  itemId: parentIssue._id,
                  boardId: issueBoard._id,
                  projectId,
                  app: AppModalEnums.ISSUE,
                }),
              )
            }
          >
            <Group gap={4} wrap="nowrap">
              <IssueIcon style={{ width: "0.9em", height: "0.9em" }} />
              <Text fw={700} size="sm">
                {parentIssue.id}
              </Text>
            </Group>
          </Button>
          <Text c="gray.7" size="sm" truncate maw="20rem">
            {parentIssue.title}
          </Text>
        </Group>
      )}

      <ScrollArea style={{ flex: 1 }}>{renderTabContent()}</ScrollArea>
    </Stack>
  );
};
